import { h } from "preact";
import { Suspense, forwardRef } from "preact/compat";
import { useContext, useMemo } from "preact/hooks";
import { MediaPlayerProps, PlayerHandler } from "./types/playerTypes";
import vendors from "./vendors";
import { VendorConfig } from "./types/configTypes";
import SettingsContext from "../SettingsContext";
import { AUDIO_EXTENSIONS } from "./helpers/patterns";
import { useRecordedURL } from "../helpers/routes";

const MediaPlayer = forwardRef<PlayerHandler<unknown>, MediaPlayerProps>(
  (props, ref) => {
    const { width, height } = props;
    const { settings } = useContext(SettingsContext);
    const threeplayHostedURL = useRecordedURL("/source");

    // TODO: AD moves in here as a regular <audio> component
    /**
     * Gets the player given the url
     */
    const getPlayer = (platform: string): VendorConfig | null => {
      for (const player of vendors) {
        if (player.key === platform) return player as VendorConfig;
      }
      return null;
    };
    const platform = getPlayer(settings.platform);
    // get the lazy file import
    const playerKey = platform?.key;
    const Player = platform?.player;

    const videoUrl = useMemo(() => {
      if (settings.platform === "native") {
        return settings.playback ?? threeplayHostedURL.toString();
      } else if (settings.platform === "YouTube") {
        return `https://www.youtube-nocookie.com/embed/${settings.mediaId}?enablejsapi=1`;
      }
    }, [settings.platform]);

    /**
     * Function for player specific styling
     */
    const getStyle = () => {
      switch (playerKey) {
        case "YouTube":
        case "Kaltura":
        case "Brightcove":
        case "Wistia":
          // This sets aspect ratio of 16:9 to give the iframe a non-zero height
          // This needs to match the aspect ratio used by the video provider
          return { "padding-bottom": "56.25%" };
        default:
          if (settings.platform === "native") {
            const shouldUseAudio =
              settings.playAs === "audio" ||
              AUDIO_EXTENSIONS.test(videoUrl || "");

            if (!shouldUseAudio) {
              return { "padding-bottom": "56.25%" };
            }
          }
          return {};
      }
    };

    if (!Player) return null;
    return (
      <div style={{ width, height, ...getStyle() }} class="MediaPlayer">
        <Suspense fallback={<div style={{ padding: "6px" }}>Loading...</div>}>
          <Player
            {...props}
            ref={ref}
            {...(videoUrl && { url: videoUrl })}
            mediaId={settings.mediaId || ""}
          />
        </Suspense>
      </div>
    );
  }
);

export default MediaPlayer;
